import { notify, RevealButton } from '@affine/component';
import {
  AuthActionStack,
  AuthContainer,
  AuthContent,
  AuthHeader,
  AuthInput,
} from '@affine/component/auth-components';
import { Button } from '@affine/component/ui/button';
import { Checkbox } from '@affine/component/ui/checkbox';
import { OtpCodeInput } from '@affine/core/components/affine/auth/otp-code-input';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { AuthService, GraphQLService } from '@affine/core/modules/cloud';
import type { AuthSessionStatus } from '@affine/core/modules/cloud/entities/session';
import { Unreachable } from '@affine/env/constant';
import {
  sendEmailOtpMutation,
  verifyBackupCodeLoginMutation,
  verifyEmailOtpLoginMutation,
  verifyTotpLoginMutation,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { AuthJourneyProgress } from '../auth-journey';
import type { SignInState } from '.';
import { Back } from './back';
import * as style from './style.css';
import { getTwoFactorVerificationErrorMessage } from './two-factor-error';

type TwoFactorMode = 'totp' | 'email' | 'backup';

export const SignInWithTotpStep = ({
  state,
  changeState,
  onAuthenticated,
  showBack = true,
}: {
  state: SignInState;
  changeState: Dispatch<SetStateAction<SignInState>>;
  onAuthenticated?: (status: AuthSessionStatus) => void;
  showBack?: boolean;
}) => {
  const email = state.email;
  if (!email) {
    throw new Unreachable();
  }

  const preferredMethod = state.preferredTwoFactorMethod ?? 'totp';

  const [mode, setMode] = useState<TwoFactorMode>(preferredMethod);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | undefined>();
  const [isVerifying, setIsVerifying] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const [resendCountDown, setResendCountDown] = useState(0);
  const emailOtpSent = useRef(false);

  const t = useI18n();
  const authService = useService(AuthService);
  const graphqlService = useService(GraphQLService);
  const loginStatus = useLiveData(authService.session.status$);

  useEffect(() => {
    onAuthenticated?.(loginStatus);
  }, [loginStatus, onAuthenticated]);

  // Resend countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setResendCountDown(c => Math.max(c - 1, 0));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const sendEmailOtp = useAsyncCallback(async () => {
    try {
      setResendCountDown(60);
      await graphqlService.gql({ query: sendEmailOtpMutation });
    } catch (err) {
      console.error('Failed to send email OTP:', err);
      notify.error({
        title: t['com.streamlain.auth.two-factor.email-otp.send-failed'](),
      });
    }
  }, [graphqlService, t]);

  // Auto-send email OTP when switching to email mode
  useEffect(() => {
    if (mode === 'email' && !emailOtpSent.current) {
      emailOtpSent.current = true;
      sendEmailOtp();
    }
  }, [mode, sendEmailOtp]);

  const onCodeChanged = useCallback((value: string) => {
    setCode(value);
    setCodeError(undefined);
  }, []);

  const onVerify = useAsyncCallback(
    async (codeOverride?: string) => {
      if (isVerifying) return;

      const codeToVerify = codeOverride ?? code;
      const expectedLength = mode === 'backup' ? 8 : 6;
      const pattern = mode === 'backup' ? /^[a-z0-9]{8}$/i : /^[0-9]{6}$/;

      if (
        codeToVerify.length !== expectedLength ||
        !pattern.test(codeToVerify)
      ) {
        setCodeError(
          mode === 'backup'
            ? t['com.streamlain.auth.two-factor.backup-code.invalid']()
            : mode === 'email'
              ? t['com.streamlain.auth.two-factor.email-code.invalid']()
              : t['com.streamlain.auth.sign.auth.code.invalid']()
        );
        return;
      }

      setIsVerifying(true);
      try {
        let result: { success: boolean; backupCodesRemaining?: number | null };

        if (mode === 'totp') {
          const res = await graphqlService.gql({
            query: verifyTotpLoginMutation,
            variables: { code: codeToVerify, trustDevice },
          });
          result = res.verifyTOTPLogin;
        } else if (mode === 'email') {
          const res = await graphqlService.gql({
            query: verifyEmailOtpLoginMutation,
            variables: { code: codeToVerify, trustDevice },
          });
          result = res.verifyEmailOTPLogin;
        } else {
          const res = await graphqlService.gql({
            query: verifyBackupCodeLoginMutation,
            variables: { code: codeToVerify, trustDevice },
          });
          result = res.verifyBackupCodeLogin;
        }

        if (result.success) {
          if (
            result.backupCodesRemaining != null &&
            result.backupCodesRemaining <= 2
          ) {
            notify.warning({
              title: t[
                'com.streamlain.auth.two-factor.backup-codes-remaining.title'
              ]({
                count: String(result.backupCodesRemaining),
              }),
              message:
                t[
                  'com.streamlain.auth.two-factor.backup-codes-remaining.message'
                ](),
            });
          }
          authService.completeTwoFactorLogin();
        } else {
          setCodeError(
            mode === 'email'
              ? t['com.streamlain.auth.two-factor.email-code.invalid']()
              : t['com.streamlain.auth.sign.auth.code.invalid']()
          );
        }
      } catch (err) {
        setCodeError(getTwoFactorVerificationErrorMessage(err, t));
      } finally {
        setIsVerifying(false);
      }
    },
    [authService, code, graphqlService, isVerifying, mode, t, trustDevice]
  );

  const switchToBackup = useCallback(() => {
    setMode('backup');
    setCode('');
    setCodeError(undefined);
  }, []);

  const switchToTotp = useCallback(() => {
    setMode('totp');
    setCode('');
    setCodeError(undefined);
    emailOtpSent.current = false;
  }, []);

  const maskedEmail = email.replace(
    /^(.{2})(.*)(@.*)$/,
    (_, start, middle, domain) => start + '*'.repeat(middle.length) + domain
  );

  const title =
    mode === 'totp'
      ? t['com.streamlain.auth.two-factor.title.totp']()
      : mode === 'email'
        ? t['com.streamlain.auth.two-factor.title.email']()
        : t['com.streamlain.auth.two-factor.title.backup']();

  const subtitle =
    mode === 'totp'
      ? t['com.streamlain.auth.two-factor.subtitle.totp']()
      : mode === 'email'
        ? t['com.streamlain.auth.two-factor.subtitle.email']({
            email: maskedEmail,
          })
        : t['com.streamlain.auth.two-factor.subtitle.backup']();

  return (
    <AuthContainer>
      <AuthHeader
        className={style.signInHeader}
        title={title}
        subTitle={subtitle}
      />

      <AuthContent>
        {mode === 'backup' ? (
          <AuthInput
            autoFocus
            data-testid="totp-code-input"
            placeholder={t['com.streamlain.auth.two-factor.backup-code']()}
            onChange={onCodeChanged}
            error={!!codeError}
            errorHint={codeError}
            onEnter={onVerify}
            type="text"
            required={true}
            maxLength={8}
          />
        ) : (
          <>
            <OtpCodeInput
              autoFocus
              testId="totp-code-input"
              value={code}
              onChange={onCodeChanged}
              onComplete={value => void onVerify(value)}
              disabled={isVerifying}
            />
            {codeError && (
              <div className={style.authInputError}>{codeError}</div>
            )}
          </>
        )}

        <div
          className={style.twoFactorTrustDeviceRow}
          onClick={e => {
            // Avoid double-toggle when clicking the checkbox input itself
            if ((e.target as HTMLElement).tagName === 'INPUT') return;
            setTrustDevice(prev => !prev);
          }}
        >
          <Checkbox
            checked={trustDevice}
            onChange={(
              _e: React.ChangeEvent<HTMLInputElement>,
              checked: boolean
            ) => setTrustDevice(checked)}
          />
          <span className={style.twoFactorTrustDeviceLabel}>
            {t['com.streamlain.auth.two-factor.trust-device']()}
          </span>
        </div>

        <div className={`${style.authMessage} ${style.twoFactorFooterLinks}`}>
          {mode === 'totp' && (
            <span
              className="link"
              data-testid="switch-to-backup-code"
              onClick={switchToBackup}
            >
              {t['com.streamlain.auth.two-factor.use-backup-code']()}
            </span>
          )}
          {mode === 'email' && (
            <>
              <span
                className="link"
                data-testid="switch-to-totp"
                onClick={switchToTotp}
              >
                {t['com.streamlain.auth.two-factor.use-authenticator']()}
              </span>
              {' · '}
              <span
                className="link"
                data-testid="switch-to-backup-code"
                onClick={switchToBackup}
              >
                {t['com.streamlain.auth.two-factor.use-backup-code']()}
              </span>
            </>
          )}
          {mode === 'backup' && (
            <span
              className="link"
              data-testid="switch-to-totp"
              onClick={switchToTotp}
            >
              {t['com.streamlain.auth.two-factor.back-to-authenticator']()}
            </span>
          )}
        </div>
        <AuthActionStack
          back={showBack ? <Back changeState={changeState} /> : undefined}
          progress={<AuthJourneyProgress />}
        >
          <RevealButton
            data-testid="verify-2fa-button"
            label={t['com.streamlain.auth.two-factor.verify']()}
            onClick={() => onVerify()}
            disabled={!!codeError || isVerifying}
            loading={isVerifying}
          />

          {mode === 'email' && (
            <Button
              disabled={resendCountDown > 0}
              variant="plain"
              onClick={sendEmailOtp}
              style={{ padding: '4px' }}
            >
              {resendCountDown <= 0
                ? t['com.streamlain.auth.two-factor.email-code.resend']()
                : t['com.streamlain.auth.sign.auth.code.resend.hint']({
                    second: String(resendCountDown),
                  })}
            </Button>
          )}
        </AuthActionStack>
      </AuthContent>
    </AuthContainer>
  );
};
